import { buildReturnStatusUrl, type ReturnStatusUrlOptions } from './shared/routing';
import { fetchDemoSession } from './demo/demo-api';
import { buildDemoModalContext } from './fluentcart/fluentcart-demo-context';
import { cacheDomElements } from './fluentcart/fluentcart-dom';
import { getFluentCartModalHtml } from './fluentcart/fluentcart-template';
import { clearDemoCheckoutFields } from './fluentcart/fluentcart-renderer';
import { renderModal, bindModalEvents } from './fluentcart/fluentcart-modal-controller';
import { createFluentCartStore, resolveMonthlyAmount, type FluentCartStore } from './fluentcart/fluentcart-state-manager';
import {
  castBaseVoteAndOptionallyDonate,
  castRetroactiveAndSubmit,
  castRetroactiveVote,
  castStandardVote,
  submitDonation,
  type FluentCartCheckoutDeps,
} from './fluentcart/fluentcart-checkout-orchestrator';
import { logClientEvent } from './fluentcart/fluentcart-telemetry';
import type { DemoCheckoutPurpose } from '../types/pifster-contracts';
import type { FluentCartModalContext } from './fluentcart/fluentcart-modal-context';

const ROOT_SELECTOR = '[data-pifster-standalone-checkout]';

function buildBaseContext(root: HTMLElement): Partial<FluentCartModalContext> {
  return {
    charityId: null,
    charityName: '',
    periodId: null,
    intentToken: null,
    beneficiary: null,
    donateHrefFallback: root.dataset['pifsterFallbackUrl'] ?? '',
    mode: 'donate_only',
    entry: 'standalone_platform_checkout',
  };
}

function initializeAmountDefaults(store: FluentCartStore): void {
  const stateConfig = store.syncResolvedState();
  const { state } = store;
  state.minAmount = stateConfig.minAmount;
  state.frequency = stateConfig.defaultFrequency;
  state.givingCadence = stateConfig.showTierSelector ? stateConfig.defaultCadence : null;
  const baseAmount = Math.max(stateConfig.minAmount, 0);
  state.amount = state.frequency === 'monthly' && stateConfig.showTierSelector
    ? resolveMonthlyAmount(state.givingCadence, baseAmount)
    : baseAmount;
}

let nextStandaloneInstanceId = 1;

function initializeStandaloneCheckout(root: HTMLElement): void {
  if (root.dataset['pifsterStandaloneInitialized'] === '1') {
    return;
  }

  const mount = root.querySelector<HTMLElement>('[data-pifster-checkout-mount]');
  if (!mount) {
    return;
  }

  root.dataset['pifsterStandaloneInitialized'] = '1';
  const idSuffix = `-standalone-${nextStandaloneInstanceId}`;
  nextStandaloneInstanceId += 1;
  mount.innerHTML = getFluentCartModalHtml(idSuffix);
  mount.classList.add('pifster-donation-modal');
  mount.dataset['pifsterStandaloneForm'] = '1';
  mount.hidden = true;
  mount.setAttribute('aria-busy', 'true');
  const store = createFluentCartStore();
  const elements = cacheDomElements(mount, idSuffix);
  const modalElements = elements;

  const closeForm = (): void => {
    root.dataset['pifsterCheckoutClosed'] = '1';
  };

  const createCheckoutDeps = (): FluentCartCheckoutDeps => ({
    setStatus(message: string): void {
      if (modalElements.statusEl) {
        modalElements.statusEl.textContent = message;
      }
    },
    setButtonsDisabled(disabled: boolean): void {
      if (modalElements.primaryButtonEl) {
        modalElements.primaryButtonEl.disabled = disabled;
      }
      if (modalElements.secondaryButtonEl) {
        modalElements.secondaryButtonEl.disabled = disabled;
      }
    },
    logEvent(name: string, payload: unknown): void {
      logClientEvent(store.getSnapshot(), name, payload as Record<string, unknown> | undefined);
    },
    redirectTo(url: string): void {
      window.location.href = url;
    },
    buildReturnStatusUrl(options: ReturnStatusUrlOptions): string {
      return buildReturnStatusUrl(store.getReturnStatusBaseUrl(), options);
    },
    showDemoCheckout(purpose: DemoCheckoutPurpose): void {
      store.state.demoCheckoutPurpose = purpose;
      renderModal(store, modalElements);
    },
    isDemoCheckoutComplete(): boolean {
      return [
        modalElements.demoCardNumberEl?.value.trim() ?? '',
        modalElements.demoExpiryEl?.value.trim() ?? '',
        modalElements.demoCvcEl?.value.trim() ?? '',
      ].every((value) => value !== '');
    },
    onDemoMembershipComplete(): void {
      clearDemoCheckoutFields(modalElements);
      store.state.demoCheckoutPurpose = null;
      store.state.eligible = true;
      store.state.frequency = 'one_time';
      store.state.givingCadence = null;
      store.state.amount = Math.max(1, store.config.minAmount);
      renderModal(store, modalElements);
    },
    clearDemoCheckoutFields(): void {
      clearDemoCheckoutFields(modalElements);
    },
    closeModal(): void {
      closeForm();
    },
  });

  bindModalEvents(mount, elements, store, {
    closeModal: closeForm,
    switchToVotingFlow(): void {
      closeForm();
    },
    submitDonation(): void {
      void submitDonation(store.state, store.config, store.config.productIds, createCheckoutDeps());
    },
    castStandardVote(): void {
      void castStandardVote(store.state, store.config.apiBase, store.getBootstrapNonce(), createCheckoutDeps());
    },
    castBaseVoteAndSubmit(): void {
      void castBaseVoteAndOptionallyDonate(
        store.state,
        store.config,
        store.config.productIds,
        store.config.apiBase,
        store.getBootstrapNonce(),
        createCheckoutDeps(),
      );
    },
    castRetroactiveVote(): void {
      void castRetroactiveVote(store.state, store.config.apiBase, store.getBootstrapNonce(), createCheckoutDeps());
    },
    castRetroactiveAndSubmit(): void {
      void castRetroactiveAndSubmit(
        store.state,
        store.config,
        store.config.productIds,
        store.config.apiBase,
        store.getBootstrapNonce(),
        createCheckoutDeps(),
      );
    },
  });

  if (elements.secondaryButtonEl) {
    elements.secondaryButtonEl.classList.add('pifster-standalone-checkout__secondary');
  }
  mount.querySelector<HTMLElement>('.c-modal__close')?.classList.add('pifster-standalone-checkout__close');
  mount.querySelector<HTMLElement>('.c-modal__overlay')?.classList.add('pifster-standalone-checkout__overlay');
  const dialog = mount.querySelector<HTMLElement>('.c-modal__dialog');
  dialog?.setAttribute('role', 'group');
  dialog?.removeAttribute('aria-modal');

  const baseContext = buildBaseContext(root);
  void fetchDemoSession().then((demoSession) => {
    const context = demoSession ? buildDemoModalContext(baseContext, demoSession) : baseContext;
    store.initializeModalState(context);
    initializeAmountDefaults(store);
    renderModal(store, modalElements);
    mount.hidden = false;
    mount.removeAttribute('aria-busy');
    root.dataset['pifsterFluentcartReady'] = '1';
  }).catch(() => {
    mount.innerHTML = '';
    const error = document.createElement('p');
    error.className = 'pifster-standalone-checkout__error';
    error.textContent = 'Giving options are temporarily unavailable. ';
    const fallbackUrl = root.dataset['pifsterFallbackUrl']?.trim() ?? '';
    if (fallbackUrl !== '') {
      const link = document.createElement('a');
      link.href = fallbackUrl;
      link.textContent = 'Continue to join';
      error.append(link);
    }
    mount.append(error);
    mount.hidden = false;
    mount.removeAttribute('aria-busy');
  });
}

function boot(): void {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach((root) => {
    initializeStandaloneCheckout(root);
  });
}

let bootScheduled = false;

function scheduleBoot(): void {
  if (bootScheduled) {
    return;
  }

  bootScheduled = true;
  window.setTimeout((): void => {
    bootScheduled = false;
    boot();
  }, 0);
}

function hasNewStandaloneMount(records: readonly MutationRecord[]): boolean {
  return records.some((record) => Array.from(record.addedNodes).some((node) => {
    if (!(node instanceof Element)) {
      return false;
    }

    return node.matches(ROOT_SELECTOR) || node.querySelector(ROOT_SELECTOR) !== null;
  }));
}

function observeLateMounts(): void {
  if (typeof MutationObserver === 'undefined' || !document.body) {
    return;
  }

  const observer = new MutationObserver((records): void => {
    if (hasNewStandaloneMount(records)) {
      scheduleBoot();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function start(): void {
  boot();
  observeLateMounts();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}

export { boot, initializeStandaloneCheckout };
